import { useAuthStore } from '@/stores/auth.store';
import { useSyncStore } from '@/stores/sync.store';
import { MAX_ATTACHMENT_SIZE, formatBytes, extractErrorMessage } from '@/lib/utils';

export interface UploadResult {
    url: string;
    filename: string;
    size: number;
    mimeType: string;
}

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3006/api';
async function compressImage(file: File, preservePng = false): Promise<Blob> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.src = URL.createObjectURL(file);
        img.onload = () => {
            const canvas = document.createElement('canvas');
            let width = img.width;
            let height = img.height;

            const MAX_WIDTH = 1200;
            const MAX_HEIGHT = 1200;

            if (width > height) {
                if (width > MAX_WIDTH) {
                    height *= MAX_WIDTH / width;
                    width = MAX_WIDTH;
                }
            } else {
                if (height > MAX_HEIGHT) {
                    width *= MAX_HEIGHT / height;
                    height = MAX_HEIGHT;
                }
            }

            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                resolve(file);
                return;
            }
            ctx.drawImage(img, 0, 0, width, height);

            const exportMime = (preservePng && file.type === 'image/png') ? 'image/png' : 'image/jpeg';
            
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        resolve(file);
                    }
                },
                exportMime,
                exportMime === 'image/jpeg' ? 0.7 : undefined
            );
        };
        img.onerror = (error) => reject(error);
    });
}

export async function uploadFile(file: File, options?: { preservePng?: boolean }): Promise<UploadResult> {
    if (file.size > MAX_ATTACHMENT_SIZE) {
        throw new Error(
            `Tệp "${file.name}" (${formatBytes(file.size)}) vượt quá dung lượng tối đa cho phép (tối đa 100MB). Vui lòng nén file hoặc chia sẻ liên kết Google Drive.`
        );
    }

    let fileToUpload: File | Blob = file;
    const isPng = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
    
    if (file.type.startsWith('image/')) {
        if (options?.preservePng && isPng) {
            // Bypass compression entirely to preserve 100% original PNG data and alpha channel
            fileToUpload = file;
        } else {
            fileToUpload = await compressImage(file, options?.preservePng);
        }
    }
    const formData = new FormData();
    formData.append('file', fileToUpload, file.name);

    const token = useAuthStore.getState().accessToken || '';
    useSyncStore.getState().beginTask('Đang tải tệp lên...');
    let res: Response;
    try {
        res = await fetch(`${API_BASE}/upload`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
        });
    } catch (networkErr: unknown) {
        useSyncStore.getState().completeTask(false);
        const errObj = networkErr as { name?: string; message?: string } | null;
        if (errObj?.name === 'TypeError' || errObj?.message === 'Failed to fetch') {
            throw new Error('Không thể kết nối tải tệp lên máy chủ hoặc tệp bị chặn bởi kết nối mạng. Vui lòng thử lại hoặc giảm kích thước tệp.');
        }
        throw new Error(extractErrorMessage(networkErr, 'Lỗi kết nối tải tệp lên máy chủ'));
    }
    if (!res.ok) {
        useSyncStore.getState().completeTask(false);
        const err = await res.json().catch(() => null);
        const errMsg = err?.message || err?.error || `Upload failed: ${res.statusText} (${res.status})`;
        throw new Error(errMsg);
    }
    
    const json = await res.json();
    useSyncStore.getState().completeTask(true, 'Đã tải tệp lên');
    const resultUrl = json?.data?.fileUrl || json?.fileUrl || json?.url || '';
    const resultFilename = json?.data?.fileName || json?.fileName || json?.filename || file.name;
    
    return {
        url: getFullUrl(resultUrl),
        filename: resultFilename,
        size: file.size,
        mimeType: file.type
    };
}

/** Get full URL for a path that may be relative (/uploads/...) or absolute (http...) */
export function getFullUrl(url: string | null | undefined): string {
    if (!url) return '';
    const backendUrl = API_BASE.replace(/\/api$/, '');
    
    if (url.startsWith('http://localhost') || url.startsWith('http://127.0.0.1')) {
        try {
            const parsedUrl = new URL(url);
            return backendUrl + parsedUrl.pathname;
        } catch {
            /* ignore */
        }
    }
    
    if (url.startsWith('http') || url.startsWith('data:')) return url;
    return `${backendUrl}${url.startsWith('/') ? '' : '/'}${url}`;
}
